// --------------------------------------------------------------
// Created On: 2023-07-20
// Author: Zachary Thomas
//
// Last Modified: 2026-08-04
// Modified By: Hannah Vaughan
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, Fragment } from "react";
import useWindowDimensions from "../../../hooks/useWindowDimensions";
import {
  CONTROLLER_APP_VFD,
  CONTROLLER_APP_SOFT_START,
  CONTROLLER_APP_MODBUS_SENSOR,
  CONTROLLER_WORLDWIDE_SOFT_START,
  CONTROLLER_ACS580,
  CONTROLLER_ACS550,
  CONTROLLER_WEG_CFW11,
  CONTROLLER_CORNELL_VALVE,
  CONTROLLER_X_DRIVE,
  DEVICES_THAT_SUPPORT_REMOTE_OPERATIONS,
  DEVICES_THAT_SUPPORT_REMOTE_DEVICE_COMMANDS,
  SCADA_RTU_DEVICE_TYPE,
} from "../../../constants/miscellaneous";
import { LOCK_REMOTE_CONTROL_PERMISSION } from "../../../constants/permissions";
import styles from "./RemoteOperationPanel.module.scss";
import userHasPermission from "../../../utilities/userHasPermission";
import InfoModal from "../../../components/InfoModal/InfoModal";
import RemoteOperationDisclaimer from "./RemoteOperationDisclaimer/RemoteOperationDisclaimer";
import CommunicationModeControls from "./CommunicationModeControls/CommunicationModeControls";
import RunningStatusControls from "./RunningStatusControls/RunningStatusControls";
import SpeedChangeControls from "./SpeedChangeControls/SpeedChangeControls";
import ManualRpmControls from "./ManualRpmControls/ManualRpmControls";
import MonitoringDeviceOperationControls from "./MonitoringDeviceOperationControls/MonitoringDeviceOperationControls";
import RemoteLockControls from "./RemoteLockControls/RemoteLockControls";
import ValvePositionControls from "./ValvePositionControls/ValvePositionControls";
import DigitalOutputControls from "./DigitalOutputControls/DigitalOutputControls";

// Panel for remotely operating an asset.
export default function RemoteOperationPanel(props: Props): Component {
  const windowDimensions = useWindowDimensions();
  const [showDisclaimer, setShowDisclaimer] = useState<boolean>(false);
  const hasControllerAcs580Or550 =
    props.controllerModel !== null && [CONTROLLER_ACS580, CONTROLLER_ACS550].includes(props.controllerModel);
  const hasControllerWegCfw11OrXDrive =
    props.controllerModel !== null && [CONTROLLER_WEG_CFW11, CONTROLLER_X_DRIVE].includes(props.controllerModel);
  const hasControllerWorldwideSoftStart = props.controllerModel === CONTROLLER_WORLDWIDE_SOFT_START;
  const hasControllerApplicationSoftStart = props.controllerModel === CONTROLLER_APP_SOFT_START;
  const hasControllerApplicationModbusSensor = props.controllerApplicationType === CONTROLLER_APP_MODBUS_SENSOR;
  const hasControllerCornellValve = props.controllerModel === CONTROLLER_CORNELL_VALVE;

  return (
    <div className={styles.body}>
      <div className={styles.titleBar}>
        <label className={styles.title}>{props.assetName} Remote Operation</label>
      </div>

      {/* Many remote operations require a controller. */}
      {DEVICES_THAT_SUPPORT_REMOTE_OPERATIONS.includes(props.deviceType || "") &&
        (hasControllerAcs580Or550 ||
          hasControllerWegCfw11OrXDrive ||
          hasControllerWorldwideSoftStart ||
          (!hasControllerApplicationModbusSensor && !hasControllerCornellValve) ||
          (!hasControllerApplicationSoftStart && !hasControllerApplicationModbusSensor && !hasControllerCornellValve) ||
          hasControllerCornellValve) && (
          <Fragment>
            {/* Special controls for the ACS580 and ACS550 VFDs. */}
            {hasControllerAcs580Or550 && (
              <CommunicationModeControls
                modes={[
                  {
                    value: "Modbus Control",
                    label: "Modbus",
                  },
                  {
                    value: "Analog/Digital Control",
                    label: "Analog/Digital",
                  },
                ]}
                disabled={props.isMigrating || !props.remoteOperationSupported || props.loading}
                onChangeCommunicationMode={(communicationMode) => props.onChangeCommunicationMode(communicationMode)}
              />
            )}

            {/* WEG only has a Modbus control option. */}
            {hasControllerWegCfw11OrXDrive && (
              <CommunicationModeControls
                modes={[
                  {
                    value: "Modbus Control",
                    label: "Modbus",
                  },
                ]}
                disabled={props.isMigrating || !props.remoteOperationSupported || props.loading}
                onChangeCommunicationMode={(communicationMode) => props.onChangeCommunicationMode(communicationMode)}
              />
            )}

            {/* Special controls for the Worldwide Soft Start. */}
            {props.controllerModel !== null && hasControllerWorldwideSoftStart && (
              <CommunicationModeControls
                modes={[
                  {
                    value: "Remote",
                    label: "Remote",
                  },
                  {
                    value: "Local",
                    label: "Local",
                  },
                ]}
                disabled={props.isMigrating || !props.remoteOperationSupported || props.loading}
                onChangeCommunicationMode={(communicationMode) => props.onChangeCommunicationMode(communicationMode)}
              />
            )}

            {props.controllerModel !== null && !hasControllerApplicationModbusSensor && !hasControllerCornellValve && (
              <RunningStatusControls
                controllerModel={props.controllerModel}
                disabled={props.isMigrating || !props.remoteOperationSupported || props.loading}
                onChangeRunningStatus={(runningStatus) => props.onChangeRunningStatus(runningStatus)}
              />
            )}

            {/* Soft starts / modbus sensors do not support speed change. */}
            {!hasControllerApplicationSoftStart &&
              !hasControllerApplicationModbusSensor &&
              !hasControllerCornellValve &&
              props.controllerModel !== null && (
                <SpeedChangeControls
                  disabled={props.isMigrating || !props.remoteOperationSupported || props.loading}
                  defaultSpeeds={
                    props.controllerApplicationType === CONTROLLER_APP_VFD
                      ? [1000, 1200, 1300, 1400, 1500, 1600, 1800, 2000]
                      : [800, 1000, 1200, 1300, 1400, 1500, 1600, 1800]
                  }
                  onChangeSpeed={(rpm) => props.onChangeSpeed(rpm)}
                />
              )}

            {/* Soft starts / modbus sensors do not support speed change. */}
            {!hasControllerApplicationSoftStart &&
              !hasControllerApplicationModbusSensor &&
              !hasControllerCornellValve &&
              props.controllerModel !== null && (
                <ManualRpmControls
                  disabled={props.isMigrating || !props.remoteOperationSupported || props.loading}
                  windowDimensionWidth={windowDimensions.width}
                  onChangeSpeed={(command) => props.onChangeSpeed(command)}
                  onError={(error) => props.onError(error)}
                />
              )}

            {hasControllerCornellValve && (
              <ValvePositionControls
                valves={[1, 2, 3, 4]}
                positions={[0, 25, 50, 100]}
                windowDimensionWidth={windowDimensions.width}
                disabled={props.isMigrating || !props.remoteOperationSupported || props.loading}
                onChangeValvePosition={(valveNumber, percentOpen) =>
                  props.onChangeValvePosition(valveNumber, percentOpen)
                }
                onError={(error) => props.onError(error)}
              />
            )}
          </Fragment>
        )}

      {DEVICES_THAT_SUPPORT_REMOTE_OPERATIONS.includes(props.deviceType || "") &&
        [SCADA_RTU_DEVICE_TYPE].includes(props.deviceType || "") &&
        props.digitalOutputs !== undefined &&
        props.digitalOutputs !== null &&
        props.digitalOutputs.map((output, i) => (
          <DigitalOutputControls
            key={i}
            position={output.position}
            operationLabel={output.operationLabel}
            trueLabel={output.trueLabel}
            falseLabel={output.falseLabel}
            disabled={props.isMigrating || props.loading}
            onChangeDigitalOutput={(position, state) => props.onChangeDigitalOutput(position, state)}
          />
        ))}

      {DEVICES_THAT_SUPPORT_REMOTE_DEVICE_COMMANDS.includes(props.deviceType || "") && (
        <MonitoringDeviceOperationControls
          deviceType={props.deviceType || ""}
          deviceId={props.deviceId}
          isDeviceAttached={props.isDeviceAttached}
          disabled={props.loading || props.isMigrating}
          firmwareUpdateInitialized={props.firmwareUpdateInitialized}
          onLatestFirmware={props.onLatestFirmware}
          windowDimensionWidth={windowDimensions.width}
          onClearFailedCommands={() => props.onClearFailedCommands()}
          onRestartMonitoringDevice={() => props.onRestartMonitoringDevice()}
          onReapplyConfiguration={() => props.onReapplyConfiguration()}
          onBlinkLeds={() => props.onBlinkLeds()}
          onTestConfiguration={() => props.onTestConfiguration()}
        />
      )}

      {userHasPermission([[LOCK_REMOTE_CONTROL_PERMISSION]]) && (
        <RemoteLockControls
          isLockedOut={props.isLockedOut}
          disabled={props.loading}
          windowDimensionWidth={windowDimensions.width}
          onChangeLock={(isLocked) => props.onChangeLock(isLocked)}
        />
      )}

      {(props.controllerModel !== null || props.digitalOutputs.length > 0) && (
        <label className={styles.controlLabel}>
          <InfoModal
            showModal={showDisclaimer}
            title="Remote Operation Disclaimer"
            linkMessage="(Remote Operation Disclaimer)"
            darkBackground={true}
            onClick={() => setShowDisclaimer(true)}
            onClose={() => setShowDisclaimer(false)}
          >
            <RemoteOperationDisclaimer />
          </InfoModal>
        </label>
      )}
    </div>
  );
}

interface Props {
  assetName: string;
  deviceId: number | null;
  isDeviceAttached: boolean;
  isMigrating: boolean;
  remoteOperationSupported: boolean;
  controllerApplicationType: string | null;
  controllerModel: string | null;
  deviceType: string | null;
  isLockedOut: boolean;
  canUnlock: boolean;
  loading: boolean;
  firmwareUpdateInitialized: boolean;
  onLatestFirmware: boolean | null;
  digitalOutputs: DigitalOutput[];
  onChangeCommunicationMode: (command: string) => void;
  onChangeSpeed: (command: number) => void;
  onChangeRunningStatus: (command: string) => void;
  onClearFailedCommands: () => void;
  onRestartMonitoringDevice: () => void;
  onReapplyConfiguration: () => void;
  onBlinkLeds: () => void;
  onTestConfiguration: () => void;
  onChangeLock: (isLocked: boolean) => void;
  onChangeValvePosition: (valveNumber: number, percentOpen: number) => void;
  onChangeDigitalOutput: (position: number, value: boolean) => void;
  onError: (error: string) => void;
}

interface DigitalOutput {
  position: number;
  operationLabel: string;
  trueLabel: string;
  falseLabel: string;
  currentValue: boolean | null;
  currentValueUtc: string | null;
}
